import {
  canPerform,
  isUserRole,
  type PolicyOperation,
} from "@headerhawk/core/roles";
import type { AuthMeResponse, UserRole } from "@/lib/api/client";
import { useProvisioning } from "./use-provisioning";

export interface CurrentRole {
  role: UserRole;
  /** False until provisioning has answered with the user's role. */
  isResolved: boolean;
  /**
   * Whether the current role may call `operation`, per the API's own
   * `ROLE_POLICY`. Ask about the operation a control performs rather than
   * naming a role, so the dashboard cannot drift from what the API enforces.
   */
  can: (operation: PolicyOperation) => boolean;
}

/**
 * The role a profile carries, or `viewer` when there is none yet.
 *
 * `viewer` is the safe default: until the profile loads, a control briefly
 * hides rather than being offered to someone it will refuse.
 */
export function roleFromProfile(profile: AuthMeResponse | null): UserRole {
  const role = profile?.user.role;
  return isUserRole(role) ? role : "viewer";
}

/**
 * The signed-in user's role in their tenant.
 *
 * Read from provisioning's `POST /auth/me`, which `RequireAuth` has already
 * made on every dashboard page, so this costs no request of its own.
 *
 * Hiding a control is presentation, not enforcement: the API's role policy is
 * what refuses the request, and a page can outlive a demotion, so every
 * mutation still has to show a 403 legibly (see `describeRequestError`).
 */
export function useCurrentRole(): CurrentRole {
  const { profile } = useProvisioning();
  const role = roleFromProfile(profile);
  return {
    role,
    isResolved: isUserRole(profile?.user.role),
    can: (operation) => canPerform(role, operation),
  };
}
